/**
 * Phase 49 Plan 49-02 — Optimistic TanStack Query mutation hook for SLA task tier changes.
 *
 * Cache key: ['board', projectId, audience] — mirrors useTaskMutation (D-12).
 * On mutate: snapshots board, patches task.tier in-place (no column move), calls patchTaskTier.
 * On error: restores snapshot.
 * On settled: invalidates board cache so server reconciles any concurrent edits.
 *
 * Unlike useTaskMutation.state (which moves tasks between columns), tier changes
 * patch in-place — a task's column position is unaffected by tier assignment.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BoardResponse, BoardColumn } from '../services/api/board';
import { patchTaskTier } from '../services/api/board';

export interface UpdateTaskTierInput {
  taskId: number;
  /** String to match useTaskMutation cache-key shape (board cache uses string projectId). */
  projectId: string;
  audience: 'internal' | 'external';
  tierId: number;
  /**
   * Pre-resolved tier metadata for the optimistic patch. Required because the
   * board cache stores the nested `tier` object on each TaskCard — the badge
   * + picker need orderIndex + name immediately after mutate fires, not after
   * the onSettled refetch. The picker resolves this from useProjectSlaTiers.
   */
  tierData: { id: number; orderIndex: number; name: string };
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

/**
 * Optimistic mutation hook for PATCH /api/tasks/:taskId/tier.
 *
 * Usage:
 *   const mutation = useUpdateTaskTier();
 *   mutation.mutate({ taskId, projectId, audience, tierId, tierData });
 */
export function useUpdateTaskTier() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, UpdateTaskTierInput, MutationContext>({
    mutationFn: (input) => patchTaskTier(input.taskId, input.tierId),
    onMutate: async (input) => {
      const key = ['board', input.projectId, input.audience] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardResponse>(key);
      if (previous) {
        queryClient.setQueryData<BoardResponse>(
          key,
          patchTierInBoard(previous, input.taskId, input.tierData),
        );
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      // Restore the snapshot taken in onMutate; toast surfacing is the caller's
      // responsibility via per-mutate { onError } option (mirrors useTaskMutation).
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, vars.audience], context.previous);
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, input.audience],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Pure patch helper — exported for unit testing.
// ---------------------------------------------------------------------------

/**
 * Returns a new BoardResponse with `task.tier` replaced by `tierData` on the
 * task matching `taskId`. All other tasks and columns are left unchanged.
 * If the task is not found, the original board is returned structurally
 * equivalent (columns are still mapped, but task objects are untouched).
 */
export function patchTierInBoard(
  board: BoardResponse,
  taskId: number,
  tierData: { id: number; orderIndex: number; name: string },
): BoardResponse {
  const next: BoardColumn[] = board.columns.map((col) => ({
    ...col,
    tasks: col.tasks.map((t) => (t.id === taskId ? { ...t, tier: tierData } : t)),
  }));
  return { columns: next };
}
